import React, { useState, useRef, useEffect } from 'react';

const AutocompleteInput = ({
  label,
  value = [],
  onChange,
  placeholder = "Type to search and add",
  suggestions = [],
  maxTags = null,
  hint = null,
  strictMode = false // When true, only allows selecting from suggestions (no custom values)
}) => {
  const [inputValue, setInputValue] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter suggestions based on input
  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = suggestions
        .filter(s =>
          s.toLowerCase().includes(inputValue.toLowerCase()) &&
          !value.includes(s)
        )
        .slice(0, 10); // Limit to 10 suggestions
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
    setSelectedIndex(-1);
  }, [inputValue, suggestions, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addTag = (tag) => {
    if (maxTags && value.length >= maxTags) {
      alert(`Maximum ${maxTags} items allowed`);
      return;
    }
    if (value.includes(tag)) {
      alert('This item already exists');
      return;
    }
    onChange([...value, tag]);
    setInputValue('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < filteredSuggestions.length) {
        addTag(filteredSuggestions[selectedIndex]);
      } else if (inputValue.trim() && filteredSuggestions.length === 0 && !strictMode) {
        // Allow adding custom values only if strictMode is disabled
        addTag(inputValue.trim());
      } else if (filteredSuggestions.length > 0) {
        addTag(filteredSuggestions[0]);
      } else if (strictMode && inputValue.trim()) {
        // In strict mode, show feedback when no match found
        setInputValue('');
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const handleRemove = (indexToRemove) => {
    onChange(value.filter((_, index) => index !== indexToRemove));
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const isNearLimit = maxTags && value.length >= maxTags * 0.8;

  return (
    <div style={{ marginBottom: '20px', position: 'relative' }} ref={dropdownRef}>
      {label && (
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          color: 'var(--color-text-secondary)',
          marginBottom: '8px'
        }}>
          {label}
        </label>
      )}

      <div
        onClick={handleContainerClick}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '10px',
          border: '1px solid var(--color-border-strong)',
          borderRadius: '6px',
          background: 'white',
          minHeight: '44px',
          cursor: 'text'
        }}
      >
        {value.map((tag, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 8px',
              background: 'var(--color-info-bg)',
              border: '1px solid var(--color-info-border)',
              borderRadius: '4px',
              fontSize: '13px',
              color: 'var(--color-info-fg)'
            }}
          >
            {tag}
            <button
              onClick={() => handleRemove(index)}
              style={{
                border: 'none',
                background: 'none',
                color: 'var(--color-info)',
                cursor: 'pointer',
                padding: 0,
                width: '16px',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '2px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              ×
            </button>
          </div>
        ))}

        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue && setShowSuggestions(filteredSuggestions.length > 0)}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={maxTags && value.length >= maxTags}
          style={{
            border: 'none',
            outline: 'none',
            flex: 1,
            minWidth: '200px',
            fontSize: '14px',
            padding: '4px',
            background: 'transparent'
          }}
        />
      </div>

      {showSuggestions && filteredSuggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '4px',
          background: 'white',
          border: '1px solid var(--color-border-strong)',
          borderRadius: '6px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          maxHeight: '240px',
          overflowY: 'auto',
          zIndex: 1000
        }}>
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => addTag(suggestion)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                fontSize: '14px',
                color: 'var(--color-text-secondary)',
                background: selectedIndex === index ? 'var(--color-info-bg)' : 'white',
                borderLeft: selectedIndex === index ? '3px solid var(--color-info)' : '3px solid transparent',
                transition: 'all 0.15s'
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}

      {/* Show "no matches" message in strict mode when user types but nothing matches */}
      {strictMode && inputValue.trim() && filteredSuggestions.length === 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '4px',
          background: 'var(--color-danger-bg)',
          border: '1px solid var(--color-danger-border)',
          borderRadius: '6px',
          padding: '10px 12px',
          fontSize: '13px',
          color: 'var(--color-danger-fg)',
          zIndex: 1000
        }}>
          No matches found. Please select from the available options.
        </div>
      )}

      {(hint || maxTags) && (
        <div style={{
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          marginTop: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>{hint || 'Type to search, press Enter to add, Backspace to remove'}</span>
          {maxTags && (
            <span style={{
              color: isNearLimit ? 'var(--color-danger-hover)' : 'var(--color-text-muted)',
              fontWeight: isNearLimit ? '600' : '400'
            }}>
              {value.length}/{maxTags}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
